'use client';

import { useSession, signIn, signOut } from 'next-auth/react';
import Link from 'next/link';
import { Plus, Settings } from 'lucide-react';
import { Button, SiteSwitcher } from '@/app/components';
import useSWR from 'swr';
import { useEffect, useState } from 'react';

import './Header.scss';
import { useSite } from '@/lib/site-context';

export default function Header() {
  const site = useSite();
  const isJS = site.id === 'js';

  // For py2g: use NextAuth session
  const { data: pySession, status: pyStatus } = useSession();

  // For js2g: use custom JWT session from API
  const [jsSession, setJsSession] = useState<any>(null);
  const [jsLoading, setJsLoading] = useState(true);

  // Fetch js2g session if on js variant
  useEffect(() => {
    if (isJS) {
      fetch('/api/auth/session')
        .then(res => res.json())
        .then(data => {
          setJsSession(data.user ? { user: data.user } : null);
          setJsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch js2g session:', err);
          setJsLoading(false);
        });
    }
  }, [isJS]);

  // Determine which session/status to use
  const session = isJS ? jsSession : pySession;
  const status = isJS ? (jsLoading ? 'loading' : (jsSession ? 'authenticated' : 'unauthenticated')) : pyStatus;

  // Use SWR to fetch and cache user avatar to avoid refetching on client navigation
  const fetcher = (url: string) => fetch(url).then(res => res.json());
  const { data: avatarData } = useSWR(
    session?.user?.id ? `/api/user/avatar/${session.user.id}` : null,
    fetcher,
    { revalidateOnFocus: false, revalidateOnReconnect: false, dedupingInterval: 600000 }
  );
  const userAvatar = avatarData?.thumbnail || null;

  const handleSignIn = () => {
    if (isJS) {
      // js2g: redirect to py2g for cross-domain auth
      // This will check if user is logged in on py2g, and if not, redirect to signin
      const returnDomain = typeof window !== 'undefined' ? window.location.host : 'js2g.com';
      const py2gUrl = process.env.NEXT_PUBLIC_PY2G_URL || 'https://py2g.com';
      window.location.href = `${py2gUrl}/api/auth/cross-domain-signin?return=${returnDomain}`;
    } else {
      // py2g: use NextAuth
      signIn('google', { callbackUrl: '/' });
    }
  };

  const handleSignOut = async () => {
    if (isJS) {
      // js2g: sign out via API and refresh
      await fetch('/api/auth/signout', { method: 'POST' });
      setJsSession(null);
      window.location.href = '/';
    } else {
      // py2g: use NextAuth
      signOut({ callbackUrl: '/' });
    }
  };

  return (
    <header className="site-header">
      <div className="header-content">
        <h1 className="logo">
          <Link href="/">{site.displayName}</Link>
        </h1>
        <nav className="nav-actions">
          <div className="nav-links">
            <Link href="/docs" className="nav-link">Docs</Link>
            <Link href="/about" className="nav-link">About</Link>
            <SiteSwitcher />
          </div>
          <Button href="/create" type="primary" icon={Plus}>
            {site.id === 'js' ? 'New JS Sketch' : 'Create New Sketch'}
          </Button>

          {status === 'loading' ? (
            <div className="loading-auth-skeleton">
              <div className="skeleton skeleton-avatar" />
              <div className="skeleton skeleton-username" />
              <div className="skeleton skeleton-button" />
            </div>
          ) : session ? (
            <div className="user-menu">
              <Link href={`/u/${(session.user as any)?.username || session.user?.id}`} className="user-profile">
                {userAvatar ? (
                  <div
                    className="profile-sketch-avatar"
                    dangerouslySetInnerHTML={{ __html: userAvatar }}
                    title="Your latest public sketch"
                  />
                ) : (
                  <div className="profile-default-avatar">
                    {session.user?.username?.charAt(0) || '?'}
                  </div>
                )}
                <span>{(session.user as any)?.username || session.user?.name || session.user?.email}</span>
              </Link>
              <Link href="/settings" className="settings-link" title="Settings">
                <Settings size={16} />
              </Link>
              <Button onClick={handleSignOut} type="outline">
                Sign Out
              </Button>
            </div>
          ) : (
            <Button onClick={handleSignIn} type="outline">
              Sign In
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}
